import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import './FlickerText.css';

const FlickerText = ({ text, className = '', isInView = true, delay = 0 }) => {
  // Split text into words (preserving spaces as separators)
  const words = useMemo(() => text.split(' '), [text]);

  // Flatten to chars with their global index for staggering
  const allChars = useMemo(() => {
    const result = [];
    let globalIndex = 0;
    words.forEach((word, wIdx) => {
      const wordChars = word.split('').map((char) => ({
        char,
        index: globalIndex++,
      }));
      result.push({ wordIndex: wIdx, chars: wordChars });
      if (wIdx < words.length - 1) {
        globalIndex++; // account for space
      }
    });
    return result;
  }, [words]);

  // Generate random durations/delays per character for the flicker
  const totalChars = useMemo(() => allChars.reduce((sum, w) => sum + w.chars.length, 0) + (words.length - 1), [allChars, words]);
  const charVariants = useMemo(
    () =>
      Array.from({ length: totalChars }, (_, i) => ({
        flickerDelay: delay + 0.05 * i + Math.random() * 0.3,
        flickerDuration: 0.08 + Math.random() * 0.12,
        flickerRepeat: 1 + Math.floor(Math.random() * 3),
      })),
    [totalChars, delay]
  );

  return (
    <span className={`flicker-text ${className}`} aria-label={text}>
      {allChars.map((wordGroup, wIdx) => (
        <React.Fragment key={wIdx}>
          <span className="flicker-word">
            {wordGroup.chars.map(({ char, index }) => (
              <motion.span
                key={index}
                className="flicker-char"
                aria-hidden="true"
                initial={{ opacity: 0 }}
                animate={
                  isInView
                    ? {
                        opacity: [0, 1, 0.2, 1, 0.4, 1],
                        textShadow: [
                          '0 0 0px transparent',
                          '0 0 8px rgba(124,106,239,0.6)',
                          '0 0 0px transparent',
                          '0 0 12px rgba(124,106,239,0.4)',
                          '0 0 0px transparent',
                          '0 0 0px transparent',
                        ],
                      }
                    : { opacity: 0.15 }
                }
                transition={
                  isInView
                    ? {
                        duration: charVariants[index].flickerDuration * charVariants[index].flickerRepeat * 2,
                        delay: charVariants[index].flickerDelay,
                        ease: 'easeOut',
                      }
                    : { duration: 0.3 }
                }
              >
                {char}
              </motion.span>
            ))}
          </span>
          {wIdx < allChars.length - 1 && <span className="flicker-space">{'\u00A0'}</span>}
        </React.Fragment>
      ))}
    </span>
  );
};

export default FlickerText;
